// Global offline status: "Offline" pill + auto-sync of queued sessions
// whenever the connection returns (any page, not just the monitor).
import { useEffect, useState } from 'react';
import { CloudOff, CloudUpload } from 'lucide-react';
import { toast } from 'sonner';
import { startOnlineAutoSync, getOutboxSize, syncQueue } from '@/services/offlineSync';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const postWorkout = (p) => fetch(`${API}/workouts`, {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  credentials: 'include',
  body: JSON.stringify(p),
});

export default function OfflineStatus() {
  const [online, setOnline] = useState(() => navigator.onLine !== false);
  const [pending, setPending] = useState(() => getOutboxSize());

  useEffect(() => {
    const refresh = () => setPending(getOutboxSize());
    const goOnline = async () => {
      setOnline(true);
      const before = getOutboxSize();
      if (!before) return;
      const r = await syncQueue(postWorkout);
      refresh();
      if (r.synced > 0) toast.success(`Back online — ${r.synced} session${r.synced === 1 ? '' : 's'} synced`, { id: 'offline-sync' });
    };
    const goOffline = () => { setOnline(false); refresh(); };
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    const unsub = startOnlineAutoSync(postWorkout);
    const poll = setInterval(refresh, 5000);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
      unsub();
      clearInterval(poll);
    };
  }, []);

  if (online && !pending) return null;
  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 z-[70] pointer-events-none"
      style={{ top: 'max(env(safe-area-inset-top, 0px), 6px)' }}
      data-testid="offline-status"
    >
      <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full shadow-md text-[11px] font-bold uppercase tracking-wider ${
        online ? 'bg-[#1565C0] text-white' : 'bg-zinc-900 text-white dark:bg-white dark:text-zinc-900'
      }`}>
        {online ? <CloudUpload className="w-3.5 h-3.5" /> : <CloudOff className="w-3.5 h-3.5" />}
        {online
          ? <span data-testid="offline-status-pending">Syncing {pending} session{pending === 1 ? '' : 's'}…</span>
          : <span data-testid="offline-status-offline">Offline · sessions save on this device{pending ? ` (${pending})` : ''}</span>}
      </div>
    </div>
  );
}
